import { DocumentTextIcon } from '@heroicons/react/24/outline'
import { Tab, Tabs } from '@nextui-org/react'
import OrderItem from '@scripts/frontend/features/shop/order/components/OrderItem'
import {
  FilterOrderStatusEnum,
  useOrder,
} from '@scripts/frontend/features/shop/order/libs/useOrder'
import SpinnerLoader from '@shared/components/spinner-loader/SpinnerLoader'
import ErrorBoundary from '@shared/ErrorBoundary'
import { useCallback, useState } from 'react'
import { useErrorBoundary } from 'react-error-boundary'
import toast from 'react-hot-toast'

function Content() {
  document.title = 'Order History - ASAVAGROUP'

  const { showBoundary } = useErrorBoundary()
  const [activeTab, setActiveTab] = useState<FilterOrderStatusEnum>(
    FilterOrderStatusEnum.All,
  )
  const { data, error, isLoading } = useOrder().getOrder({
    status: activeTab,
  })

  if (error) {
    toast.error(error?.message || 'Failed to fetch order history')
    showBoundary(error)
  }

  const handleOnTabChange = useCallback((key: FilterOrderStatusEnum) => {
    setActiveTab(key)
  }, [])

  function TabContent() {
    return (
      <div className="min-h-[50vh] space-y-4">
        {isLoading && <SpinnerLoader />}
        {data?.map((order, index) => (
          <OrderItem key={index} order={order} />
        ))}
        {data?.length === 0 && (
          <div className="flex h-80 items-center justify-center">
            <div>
              <DocumentTextIcon className="mx-auto mb-4 h-16 w-16 text-default-500" />
              <p className='text-default-500"'>No orders found.</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <h2 className="mb-2 text-xl font-medium uppercase">Order History</h2>
      <p>Track your orders, manage returns, and explore similar products</p>
      <section className="mt-10 md:max-w-full">
        <Tabs
          aria-label="Status"
          classNames={{
            base: 'mb-4',
            panel: 'p-10 bg-white relative',
          }}
          onSelectionChange={(key) =>
            handleOnTabChange(key as FilterOrderStatusEnum)
          }
          placement="top"
        >
          <Tab key={FilterOrderStatusEnum.All} title="All">
            <TabContent />
          </Tab>
          <Tab
            key={FilterOrderStatusEnum.PendingPayment}
            title="Pending Payment"
          >
            <TabContent />
          </Tab>
          <Tab key={FilterOrderStatusEnum.Processing} title="Processing">
            <TabContent />
          </Tab>
          <Tab key={FilterOrderStatusEnum.Completed} title="Completed">
            <TabContent />
          </Tab>
          <Tab
            key={FilterOrderStatusEnum.CancelRefunded}
            title="Cancel/Refunded"
          >
            <TabContent />
          </Tab>
        </Tabs>
      </section>
    </div>
  )
}

function OrderHistory() {
  return (
    <ErrorBoundary>
      <Content />
    </ErrorBoundary>
  )
}

export default OrderHistory
